import { Controller } from "@hotwired/stimulus"
import { post } from "./utils/api"

// Drives the on-page design setup wizard: step 1 picks how the menu is styled
// (global vs custom), step 2 optionally picks a starting theme. Each step just
// reports its result; this controller owns the flow and the skip rule. Because
// finishing reloads the page, the editor is re-rendered fresh by the server and
// needs no client state handed to it.
export default class extends Controller {
  static targets = ["modeStep", "decisionStep", "presetStep", "kindLabel"]
  static values = {
    themePresetUrl: String,
    csrfToken: String
  }

  redirectUrl = null

  connect() {
    // The wizard renders because we arrived with ?source=menu_card. Drop it from
    // the URL so a manual reload mid-wizard doesn't keep re-triggering setup.
    const url = new URL(window.location)
    if (url.searchParams.has("source")) {
      url.searchParams.delete("source")
      window.history.replaceState({}, "", url)
    }
  }

  chooseMode(event) {
    event.preventDefault()

    const button = event.currentTarget
    const url = button.dataset.url
    if (!url) return

    const editing = button.dataset.editing === "true"
    const kindLabel = button.dataset.kindLabel

    this.setStepBusy(this.modeStepTarget, true)

    post(url, { csrfToken: this.csrfTokenValue })
      .then((response) => response.json())
      .then((data) => {
        this.redirectUrl = data.redirect_url
        // Editing the kind that was already attached lets the user keep their
        // design or apply a preset; adopting/switching jumps straight to presets.
        if (editing) {
          this.showDecisionStep(kindLabel)
        } else {
          this.showPresetStep()
        }
      })
      .catch(() => this.setStepBusy(this.modeStepTarget, false))
  }

  keepEditing(event) {
    if (event) event.preventDefault()
    this.finish()
  }

  showPresets(event) {
    if (event) event.preventDefault()
    this.showPresetStep()
  }

  applyTheme(event) {
    event.preventDefault()

    const theme = event.currentTarget.dataset.theme
    if (!theme || !this.themePresetUrlValue) return this.finish()

    this.setStepBusy(this.presetStepTarget, true)

    // The wizard's preset step promises to replace the colours, so it asks for
    // a full apply (ignored without Brand Studio, where a preset always does).
    post(this.themePresetUrlValue, { body: { theme: theme, mode: "override" }, csrfToken: this.csrfTokenValue })
      .then((response) => response.json())
      .then(() => this.finish())
      .catch(() => this.finish())
  }

  skipTheme(event) {
    if (event) event.preventDefault()
    this.finish()
  }

  showDecisionStep(kindLabel) {
    if (this.hasModeStepTarget) this.modeStepTarget.hidden = true
    if (this.hasKindLabelTarget && kindLabel) this.kindLabelTarget.textContent = kindLabel
    this.decisionStepTarget.hidden = false
  }

  showPresetStep() {
    if (this.hasModeStepTarget) this.modeStepTarget.hidden = true
    if (this.hasDecisionStepTarget) this.decisionStepTarget.hidden = true
    this.presetStepTarget.hidden = false
  }

  // A full reload guarantees the editor reflects the freshly attached template
  // and any preset colours the server just wrote.
  finish() {
    if (this.redirectUrl) {
      window.location.href = this.redirectUrl
    } else {
      window.location.reload()
    }
  }

  setStepBusy(step, busy) {
    step.querySelectorAll("button").forEach((button) => {
      button.disabled = busy
      if (busy) {
        button.setAttribute("aria-busy", "true")
      } else {
        button.removeAttribute("aria-busy")
      }
    })
  }
}
